import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, Plus, Briefcase, Gavel } from 'lucide-react';
import { MatterCard } from '../components/crm/MatterCard';
import { NewMatterDialog } from '../components/crm/NewMatterDialog';
import { AdminCRMView } from '../components/crm/AdminCRMView';
import { PortfolioRiskPanel } from '../components/crm/PortfolioRiskPanel';
import { NormWatchPanel } from '../components/crm/NormWatchPanel';
import { JudgeRankingTop } from '../components/crm/JudgeRankingTop';
import { AppraisalSummary } from '../components/crm/AppraisalSummary';
import { validateActiveMatter } from '../lib/activeMatter';

export default function CRMPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const openHearing = params.get('audiencia');
  const focus = params.get('caso');
  const [matters, setMatters] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [allUsers, setAllUsers] = useState(false);

  const load = async () => {
    const [m, c] = await Promise.all([api.get('/crm/matters?limit=100'), api.get('/crm/clients?limit=100')]);
    setMatters(m.data.matters || []);
    validateActiveMatter(m.data.matters || []);
    setClients(c.data.clients || []);
    setLoading(false);
    if (focus) setTimeout(() => document.querySelector(`[data-testid="matter-card-${focus}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  };
  useEffect(() => {
    load();
    api.get('/admin/check').then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => {});
  }, [focus]); // eslint-disable-line react-hooks/exhaustive-deps

  const clientName = (id) => clients.find((c) => c.id === id)?.name || 'Cliente';
  const withJudge = matters.filter((m) => m.assigned_judge_id);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="crm-back-btn" onClick={() => navigate('/dashboard')}><Shield className="w-8 h-8 text-blue-500" /></button>
            <span className="text-xl font-bold">CRM de Casos</span>
          </div>
          <div className="flex items-center gap-2">
          <button data-testid="crm-fidem-link" onClick={() => navigate('/fidem')} className="bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold rounded-full px-4 py-2">Fidem</button>
          <button data-testid="crm-courts-link" onClick={() => navigate('/cortes')} className="border border-slate-600 hover:bg-slate-800 text-slate-200 text-sm font-semibold rounded-full px-4 py-2">Cortes y fiscales</button>
          <button data-testid="crm-team-sims-link" onClick={() => navigate('/simulaciones')} className="border border-slate-600 hover:bg-slate-800 text-slate-200 text-sm font-semibold rounded-full px-4 py-2">Simulaciones en equipo</button>
          <button data-testid="crm-calendar-link" onClick={() => navigate('/calendario-plazos')} className="border border-slate-600 hover:bg-slate-800 text-slate-200 text-sm font-semibold rounded-full px-4 py-2">Calendario de plazos</button>
          <button data-testid="crm-new-matter-btn" onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold rounded-full px-4 py-2 flex items-center gap-2">
            <Plus className="w-4 h-4" />Nuevo caso
          </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button data-testid="crm-vocabulary-link" onClick={() => navigate('/vocabulario-juridico')} className="mb-6 text-sm font-semibold text-emerald-300 hover:text-emerald-100">Vocabulario jurídico · consultar y vincular conceptos →</button>
        {!allUsers && <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-800"><p data-testid="crm-total-matters" className="text-2xl font-bold">{matters.length}</p><p className="text-xs text-slate-400">Casos</p></div>
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-800"><p className="text-2xl font-bold">{withJudge.length}</p><p className="text-xs text-slate-400">Con juez asignado</p></div>
          <AppraisalSummary matters={matters} />
        </div>}
        {!allUsers && !loading && <PortfolioRiskPanel />}
        {!allUsers && <NormWatchPanel />}
        {!allUsers && <JudgeRankingTop />}
        {isAdmin && (
          <div className="flex gap-2 mb-6" data-testid="crm-admin-toggle">
            <button data-testid="crm-view-mine-btn" onClick={() => setAllUsers(false)} className={`text-sm rounded-full px-4 py-1.5 border ${!allUsers ? 'bg-amber-500 text-slate-950 border-amber-500' : 'border-slate-600 text-slate-300'}`}>Mis casos</button>
            <button data-testid="crm-view-all-btn" onClick={() => setAllUsers(true)} className={`text-sm rounded-full px-4 py-1.5 border ${allUsers ? 'bg-amber-500 text-slate-950 border-amber-500' : 'border-slate-600 text-slate-300'}`}>Todos los usuarios (admin)</button>
          </div>
        )}
        {allUsers ? <AdminCRMView /> : loading ? (
          <p className="text-slate-400">Cargando casos...</p>
        ) : matters.length === 0 ? (
          <div data-testid="crm-empty" className="text-center py-16 border border-dashed border-slate-700 rounded-2xl">
            <Briefcase className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-300">Aún no tienes casos.</p>
            <p className="text-sm text-slate-500 mb-4">Crea uno, asigna el juez y Cerebro calculará tu probabilidad de ganar.</p>
            <button onClick={() => setOpen(true)} className="bg-amber-500 text-slate-950 font-semibold rounded-full px-4 py-2 text-sm">Crear primer caso</button>
          </div>
        ) : (
          <div data-testid="crm-matters-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {matters.map((m) => <MatterCard key={m.id} matter={m} clientName={clientName(m.client_id)} onUpdated={load} autoOpenHearing={openHearing === m.id} />)}
          </div>
        )}
        <button data-testid="crm-go-judicial-btn" onClick={() => navigate('/inteligencia-judicial')} className="mt-8 text-sm text-amber-400 flex items-center gap-2"><Gavel className="w-4 h-4" />Ver Inteligencia Judicial y estrategia de victoria →</button>
      </main>
      <NewMatterDialog open={open} onOpenChange={setOpen} clients={clients} onCreated={load} />
    </div>
  );
}
